import { useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { getPendingWrites, subscribeToPendingWrites } from '../lib/api/client';
import styles from './SavingGuard.module.css';

/**
 * The save lock. While any user-initiated write is in flight (the API
 * client's `pendingWrites`), the whole screen holds still:
 *
 * - **Pointer input** lands on a full-viewport layer above every dialog, so
 *   no field can be edited, no button pressed twice, no dialog dismissed and
 *   no nav link followed until the save answers.
 * - **Keyboard input** is swallowed in the CAPTURE phase on `window`, ahead
 *   of every handler in the page — Escape would otherwise close the dialog
 *   whose save is pending, and Enter would submit it a second time.
 * - **Leaving** is refused: `App.tsx`'s `navigate` and hash listener check
 *   the same count, and `beforeunload` asks before the tab closes or reloads
 *   mid-save.
 *
 * Mounted once, beside `App`, so it covers every screen including the
 * signed-out ones. The status region is ALWAYS mounted and only its text
 * changes, so a screen reader hears "Saving…" arrive (an announcement into a
 * region created in the same frame is routinely dropped).
 *
 * Nothing here can strand the user: the count is decremented in a `finally`
 * in the client, so a failed or timed-out save releases the lock exactly as
 * a successful one does.
 */
export function SavingGuard() {
  const { t } = useTranslation();
  const pending = useSyncExternalStore(subscribeToPendingWrites, getPendingWrites);
  const saving = pending > 0;

  useEffect(() => {
    if (!saving) return;

    function swallowKey(event: KeyboardEvent) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    function confirmLeave(event: BeforeUnloadEvent) {
      event.preventDefault();
      // Older engines show the prompt only when `returnValue` is set.
      event.returnValue = '';
    }

    window.addEventListener('keydown', swallowKey, true);
    window.addEventListener('beforeunload', confirmLeave);
    return () => {
      window.removeEventListener('keydown', swallowKey, true);
      window.removeEventListener('beforeunload', confirmLeave);
    };
  }, [saving]);

  return createPortal(
    <>
      <div className={styles['status']} role="status" data-testid="savingStatus">
        {saving ? t('saving.inProgress') : ''}
      </div>
      {saving ? (
        <div className={styles['blocker']} data-testid="savingBlocker" aria-hidden="true">
          <span className={styles['pill']}>
            <span className={styles['spinner']} />
            {t('saving.inProgress')}
          </span>
        </div>
      ) : null}
    </>,
    document.body,
  );
}
